/**
 * Exporter — PNG/PDF/LaTeX export
 * Per PLAN.md storage/Exporter.js and PRD §11
 */

import { jsPDF } from 'jspdf';

export class Exporter {
    /**
     * @param {import('../core/Canvas.js').Canvas} canvas 
     * @param {import('../models/CanvasDocument.js').CanvasDocument} document 
     */
    constructor(canvas, document) {
        this.canvas = canvas;
        this.document = document;
    }

    /**
     * Export to PNG
     * @param {number} scale - 1, 2, or 3 for resolution
     * @param {boolean} transparent - Transparent background
     */
    async exportPNG(scale = 2, transparent = false) {
        const { document, canvas } = this;
        const nodes = document.getAllNodes();

        if (nodes.length === 0) {
            console.warn('No nodes to export');
            return;
        }

        // Calculate bounds
        const bounds = this.calculateBounds(nodes);
        const padding = 40;

        const width = (bounds.maxX - bounds.minX + padding * 2) * scale;
        const height = (bounds.maxY - bounds.minY + padding * 2) * scale;

        // Create off-screen canvas
        const exportCanvas = document.createElement ? document.createElement('canvas') : new OffscreenCanvas(width, height);
        if (exportCanvas.width !== undefined) {
            exportCanvas.width = width;
            exportCanvas.height = height;
        }

        const ctx = exportCanvas.getContext('2d');
        ctx.scale(scale, scale);

        // Background
        if (!transparent) {
            ctx.fillStyle = canvas.isDark ? '#161616' : '#FEFEFE';
            ctx.fillRect(0, 0, width / scale, height / scale);
        }

        // For now, capture the current canvas
        // In a full implementation, we'd render nodes off-screen

        // Download
        const dataUrl = exportCanvas.toDataURL ? exportCanvas.toDataURL('image/png') : await exportCanvas.convertToBlob().then(b => URL.createObjectURL(b));
        this.download(dataUrl, `${document.name}-${Date.now()}.png`);
    }

    /**
     * Export to PDF
     */
    async exportPDF() {
        const { document } = this;
        const nodes = document.getAllNodes();

        if (nodes.length === 0) {
            console.warn('No nodes to export');
            return;
        }

        // Calculate bounds
        const bounds = this.calculateBounds(nodes);
        const padding = 40;

        const width = bounds.maxX - bounds.minX + padding * 2;
        const height = bounds.maxY - bounds.minY + padding * 2;

        // Create PDF
        const orientation = width > height ? 'landscape' : 'portrait';
        const pdf = new jsPDF({
            orientation,
            unit: 'pt',
            format: [width, height]
        });

        // Add LaTeX content as text (simplified)
        pdf.setFontSize(12);
        for (const node of nodes) {
            const x = node.position.x - bounds.minX + padding;
            const y = node.position.y - bounds.minY + padding + 12;
            pdf.text(node.content, x, y);
        }

        // Download
        pdf.save(`${document.name}-${Date.now()}.pdf`);
    }

    /**
     * Export to LaTeX source
     */
    exportLatex() {
        const { document } = this;
        const nodes = document.getAllNodes();
        const connections = document.getAllConnections();

        let latex = `% Proofroom Canvas Export: ${document.name}\n`;
        latex += `% Exported: ${new Date().toISOString()}\n\n`;

        // Group nodes by approximate position for organization
        const sortedNodes = [...nodes].sort((a, b) => {
            const yDiff = a.position.y - b.position.y;
            if (Math.abs(yDiff) > 50) return yDiff;
            return a.position.x - b.position.x;
        });

        for (const node of sortedNodes) {
            latex += `% Node: (x: ${Math.round(node.position.x)}, y: ${Math.round(node.position.y)})\n`;

            if (node.type === 'latex') {
                latex += `$${node.content}$\n\n`;
            } else {
                latex += `% Text: ${node.content}\n\n`;
            }
        }

        // Add connection comments
        if (connections.length > 0) {
            latex += `% Connections:\n`;
            for (const conn of connections) {
                const source = document.getNode(conn.sourceId);
                const target = document.getNode(conn.targetId);
                if (source && target) {
                    latex += `% ${conn.type}: "${source.content}" → "${target.content}"\n`;
                }
            }
        }

        // Download
        const blob = new Blob([latex], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        this.download(url, `${document.name}-${Date.now()}.tex`);
        URL.revokeObjectURL(url);
    }

    /**
     * Calculate bounds of all nodes
     * @param {Node[]} nodes 
     * @returns {{minX: number, minY: number, maxX: number, maxY: number}}
     */
    calculateBounds(nodes) {
        let minX = Infinity, minY = Infinity;
        let maxX = -Infinity, maxY = -Infinity;

        for (const node of nodes) {
            const bounds = node.getBounds();
            minX = Math.min(minX, bounds.x);
            minY = Math.min(minY, bounds.y);
            maxX = Math.max(maxX, bounds.x + bounds.width);
            maxY = Math.max(maxY, bounds.y + bounds.height);
        }

        return { minX, minY, maxX, maxY };
    }

    /**
     * Trigger file download
     * @param {string} url 
     * @param {string} filename 
     */
    download(url, filename) {
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    }
}
